import React from 'react';
import styled from 'styled-components';
import { OperationObjectiveInfo, EnrichedCompletedObjective } from '../../types/gameDetail';

const ObjectivesContainer = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 20px;
`;

const ObjectivesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ObjectiveItem = styled.div<{ $team: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'alien' || team === 'aliens') return 'rgba(255, 136, 0, 0.1)';
    return 'rgba(0, 212, 255, 0.1)';
  }};
  border: 1px solid ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'alien' || team === 'aliens') return 'rgba(255, 136, 0, 0.3)';
    return 'rgba(0, 212, 255, 0.3)';
  }};
  border-radius: 8px;
  border-left: 4px solid ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'alien' || team === 'aliens') return '#ff8800';
    return '#00d4ff';
  }};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => {
      const team = props.$team?.toLowerCase();
      if (team === 'alien' || team === 'aliens') return 'rgba(255, 136, 0, 0.15)';
      return 'rgba(0, 212, 255, 0.15)';
    }};
  }
`;

const ObjectiveIcon = styled.div<{ $team: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'alien' || team === 'aliens') return 'rgba(255, 136, 0, 0.2)';
    return 'rgba(0, 212, 255, 0.2)';
  }};
  border: 2px solid ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'alien' || team === 'aliens') return '#ff8800';
    return '#00d4ff';
  }};
  border-radius: 50%;
  color: ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'alien' || team === 'aliens') return '#ff8800';
    return '#00d4ff';
  }};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ObjectiveContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ObjectiveTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const ObjectiveDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #8bb8d8;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FactionBadge = styled.span<{ $team: string }>`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'human' || team === 'humans') return '#3b82f6';
    if (team === 'alien' || team === 'aliens') return '#ff8800';
    return '#9E9E9E';
  }};
  color: ${props => {
    const team = props.$team?.toLowerCase();
    if (team === 'human' || team === 'humans') return '#000';
    return '#fff';
  }};
  text-shadow: none;
`;

const CriticalBadge = styled.span`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(244, 67, 54, 0.2);
  color: #F44336;
`;

const PointsBadge = styled.span`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8bb8d8;
  font-size: 14px;
`;

const getFactionDisplayName = (team: string): string => {
  const t = team?.toLowerCase();
  if (t === 'human' || t === 'humans') return 'Human';
  if (t === 'alien' || t === 'aliens') return 'Alien';
  return team;
};

const getObjectiveTypeLabel = (type: string): string => {
  switch (type?.toLowerCase()) {
    case 'count': return 'Count';
    case 'time': return 'Time';
    case 'points': return 'Points';
    default: return type;
  }
};

interface UncompletedObjectivesListProps {
  allObjectives: OperationObjectiveInfo[];
  completedObjectives: EnrichedCompletedObjective[];
}

const UncompletedObjectivesList: React.FC<UncompletedObjectivesListProps> = ({
  allObjectives,
  completedObjectives
}) => {
  // Get IDs of completed objectives
  const completedObjectiveIds = new Set(completedObjectives.map(o => o.objectiveId));

  // Filter uncompleted objectives
  const uncompletedObjectives = allObjectives.filter(
    obj => !completedObjectiveIds.has(obj.objectiveId)
  );

  if (uncompletedObjectives.length === 0) {
    return (
      <ObjectivesContainer>
        <EmptyState>All objectives were completed in this mission</EmptyState>
      </ObjectivesContainer>
    );
  }

  return (
    <ObjectivesContainer>
      <ObjectivesList>
        {uncompletedObjectives.map((objective, index) => (
          <ObjectiveItem key={objective.objectiveId} $team={objective.team}>
            <ObjectiveIcon $team={objective.team}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </ObjectiveIcon>

            <ObjectiveContent>
              <ObjectiveTitle>
                {objective.objectiveName}
              </ObjectiveTitle>

              <ObjectiveDetails>
                <DetailItem>
                  <span>Type:</span>
                  <span style={{ color: '#fff' }}>{getObjectiveTypeLabel(objective.objectiveType)}</span>
                </DetailItem>

                {objective.objectiveType === 'count' && objective.targetCount && (
                  <DetailItem>
                    <span>Target:</span>
                    <span style={{ color: '#fff' }}>{objective.targetCount}</span>
                  </DetailItem>
                )}

                {objective.objectiveType === 'time' && objective.durationSeconds && (
                  <DetailItem>
                    <span>Duration:</span>
                    <span style={{ color: '#fff' }}>{Math.floor(objective.durationSeconds / 60)}m {objective.durationSeconds % 60}s</span>
                  </DetailItem>
                )}

                <FactionBadge $team={objective.team}>
                  {getFactionDisplayName(objective.team)}
                </FactionBadge>

                {objective.isCritical && (
                  <CriticalBadge>Critical</CriticalBadge>
                )}

                {objective.points > 0 && (
                  <PointsBadge>+{objective.points} pts</PointsBadge>
                )}
              </ObjectiveDetails>
            </ObjectiveContent>
          </ObjectiveItem>
        ))}
      </ObjectivesList>
    </ObjectivesContainer>
  );
};

export default UncompletedObjectivesList;
